import { useMemo } from "react"
import { Line } from "react-chartjs-2"
import { Chart, registerables } from "chart.js"
import styled from "styled-components"

Chart.register(...registerables)

const ChartContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return { r: 34, g: 197, b: 94 }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  }
}

interface Props {
  data: number[]
  labels?: string[]
  color?: string
}

export const SimpleLineChart = ({ data, labels, color = "#22C55E" }: Props) => {
  const { r, g, b } = useMemo(() => hexToRgb(color), [color])

  const chartLabels = useMemo(() => {
    if (labels?.length === data.length) {
      return labels
    }

    return data.map((_, i) => (i + 1).toString())
  }, [data, labels])

  const chartData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          label: "",
          data,
          borderColor: (context: { chart: Chart }) => {
            const ctx = context.chart.ctx
            const chart = context.chart
            const gradient = ctx.createLinearGradient(0, 0, chart.width, 0)
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`)
            gradient.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, 1)`)
            return gradient
          },
          backgroundColor: (context: { chart: Chart }) => {
            const ctx = context.chart.ctx
            const chart = context.chart
            const gradient = ctx.createLinearGradient(0, 0, 0, chart.height)
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.3)`)
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)
            return gradient
          },
          borderWidth: 2,
          tension: 0.5,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: true,
        },
      ],
    }),
    [chartLabels, data, r, g, b],
  )

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        x: { display: false, grid: { display: false }, border: { display: false } },
        y: { display: false, grid: { display: false }, border: { display: false } },
      },
    }),
    [],
  )

  return (
    <ChartContainer>
      <Line data={chartData} options={options} />
    </ChartContainer>
  )
}
